import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { groupSuggestionsByEntityOrTestamentaryEntityID } from '@/modules/aiSuggestions/aiSuggestions.utils';
import { useEditEntityTrowser } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import { PERSONAL_FAMILY_ACCOUNT_KINDS } from '@/modules/entities/entities.constants';
import {
  DirectHoldingsGroupContainer,
  DirectHoldingsGroupContainerProps,
} from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer';
import {
  DIRECT_HOLDINGS_CARDS_BY_VARIANT,
  filterHoldingsGroupsByClient,
} from '@/modules/estate/EstateDirectHoldings/EstateDirectHoldings.utils';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import {
  ContextPrimaryClient,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';
import { pluralize } from '@/utils/formatting/strings';

import { useReviewSuggestionCategoryData } from './ReviewSuggestion.hooks';
import { ReviewSuggestionLoadingState } from './ReviewSuggestion.LoadingState';
import { ReviewSuggestionProvider } from './ReviewSuggestion.provider';
import { ReviewSuggestionSectionHeader } from './ReviewSuggestion.SectionHeader';
import { getEstateReviewSuggestions } from './ReviewSuggestions.utils';

type DirectHoldingsGroup = Pick<
  DirectHoldingsGroupContainerProps,
  'clientProfile' | 'primaryClients' | 'variant'
>;

function getDirectHoldingsGroupsWithSuggestions(
  entities: EstatePage_EntityFragment[],
  primaryClients: ContextPrimaryClient[]
): DirectHoldingsGroup[] {
  const groups: DirectHoldingsGroup[] = [
    ...(primaryClients.length > 1 ? [{ variant: 'joint' as const }] : []),
    ...primaryClients.map((clientProfile) => ({
      clientProfile,
      variant: 'individual' as const,
    })),
    { primaryClients, variant: 'other' },
  ];

  return groups.filter(({ clientProfile, primaryClients, variant }) =>
    DIRECT_HOLDINGS_CARDS_BY_VARIANT[variant].some(
      (cardVariant) =>
        filterHoldingsGroupsByClient(
          entities,
          cardVariant,
          clientProfile?.id,
          primaryClients?.map((client) => client.id)
        ).length > 0
    )
  );
}

export function DirectHoldingsReviewSuggestions() {
  const editEntityTrowser = useEditEntityTrowser();
  const { defaultFirstToDieClientId, primaryClients } =
    useHouseholdDetailsContext();
  const data = useReviewSuggestionCategoryData({
    entityKinds: PERSONAL_FAMILY_ACCOUNT_KINDS,
    includeOnboardingSuggestions: false,
    includeTestamentaryEntities: false,
  });
  const estateReview = useMemo(
    () =>
      getEstateReviewSuggestions({
        entities: data.entities,
        testamentaryEntities: [],
        primaryDeathOrderClientId: defaultFirstToDieClientId ?? undefined,
        suggestionsByEntityID: groupSuggestionsByEntityOrTestamentaryEntityID(
          data.suggestions
        ),
      }),
    [data.entities, data.suggestions, defaultFirstToDieClientId]
  );
  const directHoldingsGroups = useMemo(
    () =>
      getDirectHoldingsGroupsWithSuggestions(
        estateReview.directHoldings,
        primaryClients ?? []
      ),
    [estateReview.directHoldings, primaryClients]
  );

  if (data.loading && data.entities.length === 0)
    return <ReviewSuggestionLoadingState />;

  return (
    <ReviewSuggestionProvider {...data} entities={estateReview.directHoldings}>
      <Stack spacing={2}>
        <ReviewSuggestionSectionHeader
          title={`${pluralize({
            count: directHoldingsGroups.length,
            singular: 'direct holding group',
            plural: 'direct holding groups',
          })} with suggestions`}
          description="The following direct holdings groups have outstanding suggestions."
        />
        {directHoldingsGroups.map((group) => (
          <DirectHoldingsGroupContainer
            key={group.clientProfile?.id ?? group.variant}
            {...group}
            suggestionsOnly
            onEditEntitySection={(entityId, section) =>
              editEntityTrowser.openForEntity(entityId, { section })
            }
          />
        ))}
      </Stack>
    </ReviewSuggestionProvider>
  );
}
